import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { getCookie } from '@services/axiosInstance';

const PDFViewer = forwardRef((props, ref) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const iframeRef = useRef(null);
  const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    import('./PdfPrint.css').then(() => {});
    
    // Detectar dispositivo móvel
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const loadPDF = async () => {
    try {
      // Verificar se a URL do PDF existe e é válida
      if (!props.pdfUrl || !props.pdfUrl.startsWith('http')) {
        throw new Error('URL do PDF inválida ou não definida');
      }
      
      // Get authentication headers
      const token = getCookie('token') || localStorage.getItem('token');
      const sessionId = getCookie('ePOS');
      
      const headers = {
        'Accept': 'application/pdf',
      };
      
      // Add authentication headers
      if (token) {
        headers['x-auth-token'] = token;
        headers['Authorization'] = `Bearer ${token}`;
        headers['x-epos-auth'] = token;
      }
      
      if (sessionId && !token) {
        headers['x-epos-auth'] = sessionId;
      }
      
      const response = await fetch(props.pdfUrl, {
        mode: 'cors',
        credentials: 'include',
        headers: headers
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setPdfObjectUrl(url);
      return url;
    } catch (error) {
      console.error('Error loading PDF:', error);
      alert('Failed to load PDF. Please try again.');
      throw error;
    }
  };

  const handlePrint = () => {
    return new Promise(async (resolve, reject) => {
      try {
        setIsPrinting(true);
        const url = await loadPDF();
  
        // Em dispositivos móveis, abrir PDF numa nova aba e sugerir impressão
        if (isMobile) {
          const newWindow = window.open(url, '_blank');
          if (newWindow) {
            // Dar tempo para o PDF carregar antes de sugerir impressão
            setTimeout(() => {
              try {
                newWindow.print();
              } catch (error) {
                console.log('Fallback: Open print dialog manually');
              }
            }, 1000);
            resolve('Print opened in new tab');
          } else {
            // Se bloqueou popup, apenas abrir URL diretamente
            window.open(url, '_blank');
            resolve('PDF opened in new tab - use browser print');
          }
          setIsPrinting(false);
          return;
        }
  
        // Em desktop, usar iframe como antes
        if (iframeRef.current) {
          iframeRef.current.onload = () => {
            try {
              if (iframeRef.current?.contentWindow) {
                iframeRef.current.contentWindow.focus();
                iframeRef.current.contentWindow.print();
                resolve('Print successful');
              }
            } catch (error) {
              console.error('Error printing:', error);
              alert('Failed to print. Please try again.');
              reject(error);
            } finally {
              setIsPrinting(false);
            }
          };
          iframeRef.current.src = url;
        } else {
          reject(new Error('iframeRef is not available'));
          setIsPrinting(false);
        }
      } catch (error) {
        console.error('Error in handlePrint:', error);
        setIsPrinting(false);
        reject(error);
      }
    });
  };

  const showPreview = () => {
    return new Promise(async (resolve, reject) => {
      try {
        const url = await loadPDF();
        
        // Em dispositivos móveis, abrir PDF numa nova aba
        if (isMobile) {
          window.open(url, '_blank');
          resolve('Preview opened in new tab');
          return;
        }
        
        // Em desktop, usar iframe como antes
        if (iframeRef.current) {
          iframeRef.current.src = url;
          setIsPreviewVisible(true);
          resolve('Preview shown successfully');
        } else {
          reject(new Error('iframeRef is not available'));
        }
      } catch (error) {
        console.error('Error in showPreview:', error);
        setIsPreviewVisible(false);
        reject(error);
      }
    });
  };

  useImperativeHandle(ref, () => ({
    handlePrint,
    showPreview
  }));

  React.useEffect(() => {
    return () => {
      if (pdfObjectUrl) {
        URL.revokeObjectURL(pdfObjectUrl);
      }
    };
  }, [pdfObjectUrl]);

  return (
    <>
      <iframe
        ref={iframeRef}
        style={{ display: 'none' }}
        title="Print Frame"
      />

      {isPreviewVisible && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2 className="modal-title">Pré-vizualizar</h2>
              <button
                className="close-button"
                onClick={() => {
                  setIsPreviewVisible(false);
                  if (pdfObjectUrl) {
                    URL.revokeObjectURL(pdfObjectUrl);
                    setPdfObjectUrl(null);
                  }
                }}
              >
                <svg
                  width="24"
                  height="24"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <div className="modal-content">
              <iframe
                className="pdf-iframe"
                src={pdfObjectUrl || ''}
                title="PDF Preview"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default PDFViewer;